import React, { useState } from 'react';
import { Button, Text } from '@querodelivery/uni-react';
import { format } from '@querodelivery/qd-date-helper';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import styles from './styles.module.scss';

import { moneyLabel } from '@/Utils/FnUtils';
import { dialog } from '@/infra/dialog/Dialog';
import Requests from '@/Services/Requests';
import useRequest from '@/hooks/util/useRequest';
import type { IPlaceMinimalFareInfo } from '@/Interfaces/Data';
import PlaceFareModal from '../place-fare-modal/PlaceFareModal';

interface IProps {
  fare: IPlaceMinimalFareInfo;
  updateFares(): void;
  onExportFare(placeId: string): void;
}

function PlaceFareCard({
  fare: { _id, createdAt, nomeExibicao, placeFare },
  updateFares,
  onExportFare,
}: IProps) {
  const navigate = useNavigate();

  const { isLoading, request } = useRequest();

  const [isFareModalOpened, setIsFareModalOpened] = useState(false);

  const handleUpdatePlaceFare: React.MouseEventHandler<
    HTMLButtonElement
  > = event => {
    event.stopPropagation();

    navigate(`/tarifas/estabelecimento/editar/${_id}`);
  };

  async function handleDeletePlaceFare() {
    const confirmed = await dialog.confirm(
      <div className="text-center">
        <p className="mgn-bottom-sm">
          Você tem certeza que quer excluir a tarifa <b>{nomeExibicao}</b>?
        </p>
      </div>,
      { isAlert: true, closeIcon: true },
    );

    if (!confirmed) {
      return;
    }

    try {
      await request(Requests.places.deletePlaceFare(_id));

      updateFares();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const errorMessage = err.response?.data.error;
      toast.error(errorMessage);
    }
  }

  function openFareModal() {
    setIsFareModalOpened(true);
  }

  const createdAtToShow = placeFare?.createdAt ?? createdAt;

  return (
    <>
      <div className={styles.placeFareCard} onClick={openFareModal}>
        <div className={styles.placeFareInfo}>
          <div className={styles.placeFareInfoRow}>
            <div className={styles.placeFareInfoText}>
              <Text className={styles.placeFareInfoText} variant="headingMd">
                Estabelecimento:
              </Text>{' '}
              <Text variant="bodyMd">{nomeExibicao}</Text>
            </div>

            <div className={styles.placeFareInfoText}>
              <Text className={styles.placeFareInfoText} variant="headingMd">
                Valor entrega base:
              </Text>{' '}
              <Text variant="bodyMd">
                {moneyLabel(placeFare?.valorEntregaBase!)}
              </Text>
            </div>
          </div>

          <div className={styles.placeFareInfoRow}>
            <div className={styles.placeFareInfoText}>
              <Text className={styles.placeFareInfoText} variant="headingMd">
                Data de criação:
              </Text>{' '}
              <Text variant="bodyMd">
                {format(createdAtToShow, 'dd/MM/yyyy')}
              </Text>
            </div>

            <div className={styles.placeFareInfoText}>
              <Text className={styles.placeFareInfoText} variant="headingMd">
                Taxa entrega base:
              </Text>{' '}
              <Text variant="bodyMd">
                {moneyLabel(placeFare?.taxaEntregaBase!)}
              </Text>
            </div>
          </div>
        </div>

        <div className={styles.placeFareActions}>
          <div className={styles.buttons}>
            <Button
              variant="outline"
              className={styles.button}
              onClick={event => {
                event.stopPropagation();
                onExportFare(_id);
              }}
            >
              <Text variant="bodySm" tone="supporting" fontWeight="medium">
                Exportar tarifa
              </Text>
            </Button>

            <Button
              variant="outline"
              className={styles.button}
              onClick={handleUpdatePlaceFare}
            >
              <Text variant="bodySm" tone="supporting" fontWeight="medium">
                Atualizar tarifa
              </Text>
            </Button>

            <Button
              variant="outline"
              className={styles.button}
              disabled={isLoading}
              onClick={event => {
                event.stopPropagation();
                handleDeletePlaceFare();
              }}
            >
              <Text variant="bodySm" tone="supporting" fontWeight="medium">
                Excluir tarifa
              </Text>
            </Button>
          </div>
        </div>
      </div>

      {isFareModalOpened && (
        <PlaceFareModal
          onClose={() => setIsFareModalOpened(false)}
          place={{ _id, createdAt, nomeExibicao, placeFare }}
        />
      )}
    </>
  );
}

export default PlaceFareCard;
